import React from "react";
import CountUp from "react-countup";
import SVGRenderer from "../SVGRenderer";
import LazyImage from "components/elements/LazyImage/LazyImage";
import SmartLink from "components/SmartLink";
import Button from "components/elements/Button/Button";
import { isColorDark } from "helpers/contrastCalculator";

import styles from "./Card.module.scss";

/**
 * Feature Card with elevated appearance for highlighting important content
 * 
 * @param {Object} props - Component properties
 */
const CardFeature = React.forwardRef(({ 
	inView,
	title, 
	svg, 
	lead, 
	desc, 
	cta_text, 
	cta_link, 
	image, 
	image_alt, 
	bg_image, 
	bg_image_alt, 
	bg_color, 
	className = "", 
	theme, 
	count_up
}, ref) => {
	// Extract variable name if it's a CSS variable
	const bgColorVar = bg_color && bg_color.includes('var(') 
		? bg_color.match(/var\((.*?)[,)]/)[1].trim()
		: null;
	
	// Check if background is dark (if a background color is provided)
	const isDarkBg = bgColorVar ? isColorDark(bgColorVar) : false;
	
	// Check if primary color is dark (for feature badge)
	const isPrimaryDark = isColorDark('--bs-primary');
	
	// Choose theme based on background brightness if bg_color is provided
	const cardTheme = bg_color ? (isDarkBg ? "dark" : "light") : theme;
	
	// Set CSS class for text color based on background
	const textColorClass = bgColorVar ? 
		(isDarkBg ? styles.darkText : styles.lightText) : '';
	
	return (
		<div
			ref={ref}
			data-bs-theme={cardTheme}
			className={`${styles.CardFeature} card h-100 rounded-4 shadow-lg border-0 position-relative overflow-hidden ${className} ${bg_image ? "bg-transparent" : ""} ${textColorClass}`}
			{...(bg_color && { style: { backgroundColor: bg_color } })}>
			{image && (
				<div className={styles.featureImageContainer}>
					<LazyImage
						src={image}
						alt={image_alt || title}
						className={styles.featureImage}
						fill_mode="cover"
						imgClassName="w-100 h-100"
						centerImage={true}
					/>
				</div>
			)}
			<div className="card-body p-4 d-flex flex-column">
				<div className={`${styles.featureBadge} ${isPrimaryDark ? styles.darkBadge : styles.lightBadge}`}>
					Featured
				</div>
				{svg && (
					<div className="mb-3 text-center">
						<SVGRenderer 
							svgString={svg}
							svgProps={{ className: styles.featureSvg }} 
						/>
					</div>
				)}
				{inView && count_up && (
					<CountUp
						className={`${styles.featureCountUp} display-5 fw-bold mb-3`}
						end={count_up}
						duration={5}
						separator="."
					/>
				)}
				{title && (
					<h3 className={`${styles.featureTitle} card-title fs-3 fw-bold mb-0`}>
						{title}
					</h3>
				)}
				{lead && (
					<p className={`${styles.featureLead} lead fs-5 mt-2`}>
						{lead}
					</p>
				)}

				{desc && (
					<p
						className={`${styles.featureDesc} card-text`}
						dangerouslySetInnerHTML={{ __html: desc }}></p>
				)}
				<div className="mt-auto pt-3">
					{cta_text && cta_link && (
						<SmartLink to={cta_link}>
							<Button
								size={"lg"}
								variant={isDarkBg ? "light" : "primary"}
								letterCase="uppercase"
								className="w-100">
								{cta_text}
							</Button>
						</SmartLink>
					)}
				</div>
			</div>
			{bg_image && (
				<LazyImage
					src={bg_image}
					alt={bg_image_alt || ""}
					className={styles.featureBackgroundImage}
					fill_mode="cover"
					aria-hidden="true"
				/>
			)}
		</div>
	);
});

export default CardFeature;
